/**
 * Admin Promoter Manager — Phase P1
 * ─────────────────────────────────────────────────────────────────────────
 * Route: /admin/promoters   (super_admin + manager)
 *
 * 3 tabs:
 *   • Promoters    — list + search + create + activate/suspend + ledger drilldown
 *   • Payouts      — queue of `locked` payout requests; approve / reject
 *   • Models       — commission models CRUD with per-weekday threshold editor
 */
import React, { useEffect, useMemo, useState } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { promoterApi } from '../../lib/api';
import { adminApi } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Users, BadgeIndianRupee, Settings, Plus, Search, RefreshCw,
  CheckCircle2, XCircle, Edit3, ScrollText, FileText, ExternalLink, Copy,
} from 'lucide-react';

const WEEKDAYS = [
  ['1', 'Mon'], ['2', 'Tue'], ['3', 'Wed'], ['4', 'Thu'],
  ['5', 'Fri'], ['6', 'Sat'], ['0', 'Sun'],
];
// Note: backend uses Python weekday() (Mon=0..Sun=6). UI shows Mon-first
// but stores under same numeric keys. We translate at submit time.
const PY_WEEKDAYS = [
  ['0', 'Mon'], ['1', 'Tue'], ['2', 'Wed'], ['3', 'Thu'],
  ['4', 'Fri'], ['5', 'Sat'], ['6', 'Sun'],
];

const STATUS_PILL = {
  active:    'bg-[#0E2E1E] text-[#2FBF71] border-[#1F4D33]',
  suspended: 'bg-[#3D1A1A] text-[#E66666] border-[#5A2E2E]',
  pending:   'bg-[#3D2E0F] text-[#E6B450] border-[#5A4519]',
};

// ─── Promoters tab ───────────────────────────────────────────────────────────
function PromotersTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [ledgerFor, setLedgerFor] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (q.trim())               params.q = q.trim();
      const { data } = await promoterApi.adminList(params);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to load promoters');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [statusFilter]);

  const toggleStatus = async (promoter) => {
    const next = promoter.status === 'active' ? 'suspended' : 'active';
    try {
      await promoterApi.adminUpdate(promoter._id, { status: next });
      toast.success(`Marked ${next}`);
      await fetch();
    } catch {
      toast.error('Status update failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search size={16} className="text-[#6F6F6F]" />
          <Input
            placeholder="Search code or user id…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetch()}
            className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2F2F2] max-w-xs"
            data-testid="promoter-search-input"
          />
          <Button size="sm" onClick={fetch} className="bg-[#1A1A1A] border border-[#2A2A2A] text-[#F2F2F2] hover:bg-[#2A2A2A]">
            Search
          </Button>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px] bg-[#0A0A0A] border-[#2A2A2A] text-[#F2F2F2]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#111111] border-[#2A2A2A] text-[#F2F2F2]">
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setCreateOpen(true)} className="bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08] font-medium" data-testid="create-promoter-button">
          <Plus size={16} className="mr-1" /> Add
        </Button>
      </div>

      <div className="surface-card border border-[#2A2A2A] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A]">
              <tr className="border-b border-[#2A2A2A] text-[10px] uppercase tracking-wider text-[#6F6F6F]">
                <th className="py-3 px-3 text-left">Short</th>
                <th className="py-3 px-3 text-left">Code</th>
                <th className="py-3 px-3 text-left">Customer</th>
                <th className="py-3 px-3 text-left">Status</th>
                <th className="py-3 px-3 text-right">Bookings</th>
                <th className="py-3 px-3 text-right">Revenue</th>
                <th className="py-3 px-3 text-right">Available</th>
                <th className="py-3 px-3 text-right">Pending</th>
                <th className="py-3 px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && [1, 2, 3].map((i) => (
                <tr key={i}><td colSpan="9" className="py-3 px-3"><Skeleton className="h-6 bg-[#1A1A1A]" /></td></tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan="9" className="text-center py-10 text-[#6F6F6F]">No promoters yet — click <b>Add</b> to onboard one.</td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r._id} className="border-b border-[#2A2A2A] hover:bg-[#181818]">
                  <td className="py-3 px-3 font-mono text-[#D4A843] text-lg tabular-nums" data-testid={`promoter-short-${r.short_code || 'na'}`}>
                    {r.short_code || <span className="text-[#6F6F6F] text-xs">—</span>}
                  </td>
                  <td className="py-3 px-3 font-mono text-[#B9B9B9] text-xs" data-testid={`promoter-row-${r.code}`}>{r.code}</td>
                  <td className="py-3 px-3">
                    <div className="text-[#F2F2F2]">{r.customer_name || '—'}</div>
                    <div className="text-[10px] text-[#6F6F6F]">{r.customer_phone || ''}</div>
                  </td>
                  <td className="py-3 px-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${STATUS_PILL[r.status] || STATUS_PILL.pending}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-right text-[#F2F2F2]">{r.total_bookings || 0}</td>
                  <td className="py-3 px-3 text-right text-[#F2F2F2]">{formatCurrency(r.total_revenue || 0)}</td>
                  <td className="py-3 px-3 text-right text-[#2FBF71]">{formatCurrency(r.balances?.available || 0)}</td>
                  <td className="py-3 px-3 text-right text-[#E6B450]">{formatCurrency(r.balances?.pending || 0)}</td>
                  <td className="py-3 px-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setLedgerFor(r)} className="text-[#B9B9B9] hover:text-[#F2F2F2]" title="View ledger" data-testid={`view-ledger-${r.code}`}>
                        <ScrollText size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleStatus(r)} className="text-[#B9B9B9] hover:text-[#F2F2F2]" title={r.status === 'active' ? 'Suspend' : 'Activate'} data-testid={`toggle-status-${r.code}`}>
                        {r.status === 'active' ? <XCircle size={14} /> : <CheckCircle2 size={14} />}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 text-[10px] text-[#6F6F6F] border-t border-[#2A2A2A]">{total} promoters</div>
      </div>

      <CreatePromoterDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={fetch} />
      {ledgerFor && <PromoterLedgerDialog promoter={ledgerFor} onClose={() => setLedgerFor(null)} />}
    </div>
  );
}

// ─── Create Promoter dialog ──────────────────────────────────────────────────
function CreatePromoterDialog({ open, onClose, onCreated }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [vanity, setVanity] = useState('');
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (!q.trim()) return;
    try {
      const { data } = await adminApi.listCustomers({ search: q.trim(), limit: 8 });
      setResults(data.customers || []);
    } catch {
      toast.error('Customer search failed');
    }
  };

  const submit = async () => {
    if (!picked) { toast.error('Pick a customer first'); return; }
    setBusy(true);
    try {
      await promoterApi.adminCreate({
        user_id:     picked._id,
        vanity_code: vanity.trim() || null,
      });
      toast.success('Promoter created');
      onCreated?.();
      onClose();
      setQ(''); setVanity(''); setPicked(null); setResults([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111111] border-[#2A2A2A] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#F2F2F2]">Add Promoter</DialogTitle>
          <DialogDescription className="text-[#B9B9B9]">
            Pick an existing customer. They will start earning commission on the next booking.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or phone" onKeyDown={(e) => e.key === 'Enter' && search()} className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2F2F2]" />
            <Button onClick={search} className="bg-[#1A1A1A] border border-[#2A2A2A] text-[#F2F2F2] hover:bg-[#2A2A2A]">Search</Button>
          </div>
          <div className="max-h-44 overflow-y-auto space-y-1">
            {results.map((c) => (
              <button
                key={c._id}
                onClick={() => setPicked(c)}
                className={`w-full text-left px-3 py-2 rounded border ${picked?._id === c._id ? 'bg-[#D4A843]/10 border-[#D4A843]/50' : 'bg-[#0A0A0A] border-[#2A2A2A] hover:bg-[#1A1A1A]'} transition-colors`}
                data-testid={`customer-pick-${c._id}`}
              >
                <div className="text-[#F2F2F2] text-sm">{c.full_name || 'Unnamed'}</div>
                <div className="text-[10px] text-[#6F6F6F]">{c.phone_e164 || c.email || c._id}</div>
              </button>
            ))}
          </div>
          <div>
            <Label className="text-xs text-[#B9B9B9]">Vanity code (optional)</Label>
            <Input value={vanity} onChange={(e) => setVanity(e.target.value.toUpperCase())} placeholder="ARJUN, NEHA-MUM…" className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2F2F2] uppercase mt-1" />
            <p className="text-[10px] text-[#6F6F6F] mt-1">Leave blank to auto-generate (PRM-XXXXX).</p>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={!picked || busy} className="bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08] font-medium" data-testid="create-promoter-submit">
            {busy ? 'Creating…' : 'Create Promoter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Promoter Ledger Drilldown ───────────────────────────────────────────────
function PromoterLedgerDialog({ promoter, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    promoterApi.adminLedger(promoter._id, { limit: 200 })
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Failed to load ledger'));
  }, [promoter._id]);
  return (
    <Dialog open={!!promoter} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111111] border-[#2A2A2A] max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-[#F2F2F2] font-mono text-[#D4A843]">{promoter.code} · Ledger</DialogTitle>
          <DialogDescription className="text-[#B9B9B9]">
            {data ? `${data.total} entries · Available ${formatCurrency(data.balances.available)} · Pending ${formatCurrency(data.balances.pending)}` : 'Loading…'}
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#0A0A0A] z-10">
              <tr className="border-b border-[#2A2A2A] text-[10px] uppercase tracking-wider text-[#6F6F6F]">
                <th className="py-2 px-2 text-left">Date</th>
                <th className="py-2 px-2 text-left">Type</th>
                <th className="py-2 px-2 text-left">Status</th>
                <th className="py-2 px-2 text-left">Booking</th>
                <th className="py-2 px-2 text-right">Rate</th>
                <th className="py-2 px-2 text-right">Revenue</th>
                <th className="py-2 px-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data?.rows?.map((r) => (
                <tr key={r._id} className="border-b border-[#1A1A1A] hover:bg-[#181818]">
                  <td className="py-2 px-2 text-[10px] text-[#6F6F6F]">{r.created_at ? new Date(r.created_at).toLocaleString('en-IN') : ''}</td>
                  <td className="py-2 px-2 text-[#F2F2F2]">{r.type}</td>
                  <td className="py-2 px-2 text-[#B9B9B9]">{r.status}</td>
                  <td className="py-2 px-2 font-mono text-[10px] text-[#6F6F6F]">{r.reference_booking_id?.slice(-8) || '—'}</td>
                  <td className="py-2 px-2 text-right text-[#B9B9B9]">{r.computed_rate ? `${r.computed_rate}%` : '—'}</td>
                  <td className="py-2 px-2 text-right text-[#B9B9B9]">{r.computed_revenue ? formatCurrency(r.computed_revenue) : '—'}</td>
                  <td className={`py-2 px-2 text-right font-semibold ${r.amount > 0 ? 'text-[#2FBF71]' : r.amount < 0 ? 'text-[#E66666]' : 'text-[#B9B9B9]'}`}>
                    {r.amount > 0 ? '+' : ''}{formatCurrency(r.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Payouts tab ─────────────────────────────────────────────────────────────
function PayoutsTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('locked');
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null);   // { row, action: 'approve'|'reject' }
  const [decisionInput, setDecisionInput] = useState('');

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await promoterApi.adminPayoutsList({ status: filter });
      setRows(data.rows || []);
    } catch {
      toast.error('Failed to load payouts');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [filter]);

  const submitDecision = async () => {
    if (!decision) return;
    try {
      if (decision.action === 'approve') {
        await promoterApi.adminPayoutApprove(decision.row._id, { payment_reference: decisionInput.trim() || null });
        toast.success('Payout approved & marked paid');
      } else {
        await promoterApi.adminPayoutReject(decision.row._id, { reason: decisionInput.trim() || null });
        toast.success('Payout rejected; funds returned to available');
      }
      setDecision(null);
      setDecisionInput('');
      await fetch();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || 'Decision failed');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[160px] bg-[#0A0A0A] border-[#2A2A2A] text-[#F2F2F2]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#111111] border-[#2A2A2A] text-[#F2F2F2]">
            <SelectItem value="locked">Pending review</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="cancelled">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="ghost" onClick={fetch} className="text-[#B9B9B9] hover:text-[#F2F2F2]"><RefreshCw size={14} /></Button>
      </div>
      <div className="surface-card border border-[#2A2A2A] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A]">
            <tr className="border-b border-[#2A2A2A] text-[10px] uppercase tracking-wider text-[#6F6F6F]">
              <th className="py-3 px-3 text-left">Requested</th>
              <th className="py-3 px-3 text-left">Promoter</th>
              <th className="py-3 px-3 text-left">Customer</th>
              <th className="py-3 px-3 text-right">Amount</th>
              <th className="py-3 px-3 text-left">Status</th>
              <th className="py-3 px-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && [1, 2].map((i) => (
              <tr key={i}><td colSpan="6" className="py-3 px-3"><Skeleton className="h-6 bg-[#1A1A1A]" /></td></tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="6" className="text-center py-10 text-[#6F6F6F]">No requests.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r._id} className="border-b border-[#2A2A2A] hover:bg-[#181818]">
                <td className="py-3 px-3 text-[10px] text-[#6F6F6F]">{new Date(r.created_at).toLocaleString('en-IN')}</td>
                <td className="py-3 px-3 font-mono text-[#D4A843]">{r.promoter_code}</td>
                <td className="py-3 px-3">
                  <div className="text-[#F2F2F2]">{r.customer_name || '—'}</div>
                  <div className="text-[10px] text-[#6F6F6F]">{r.customer_phone || ''}</div>
                </td>
                <td className="py-3 px-3 text-right text-[#F2F2F2] font-semibold">{formatCurrency(r.amount)}</td>
                <td className="py-3 px-3 text-[#B9B9B9]">{r.status}</td>
                <td className="py-3 px-3 text-right">
                  {r.status === 'locked' ? (
                    <div className="flex justify-end gap-1">
                      <Button size="sm" onClick={() => { setDecision({ row: r, action: 'approve' }); setDecisionInput(''); }} className="bg-[#2FBF71] hover:bg-[#3FCF81] text-[#0A1A0E] h-7 px-2 text-xs" data-testid={`approve-payout-${r._id}`}>
                        Approve
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setDecision({ row: r, action: 'reject' }); setDecisionInput(''); }} className="text-[#E66666] hover:bg-[#3D1A1A] h-7 px-2 text-xs" data-testid={`reject-payout-${r._id}`}>
                        Reject
                      </Button>
                    </div>
                  ) : (
                    r.payment_reference && <span className="text-[10px] text-[#6F6F6F] font-mono">{r.payment_reference}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent className="bg-[#111111] border-[#2A2A2A]">
          <DialogHeader>
            <DialogTitle className="text-[#F2F2F2]">
              {decision?.action === 'approve' ? 'Approve' : 'Reject'} Payout · {decision?.row?.promoter_code}
            </DialogTitle>
            <DialogDescription className="text-[#B9B9B9]">
              {decision?.action === 'approve'
                ? `Mark ${formatCurrency(decision?.row?.amount || 0)} as paid. Optional UTR/payment reference will be stored on the ledger row.`
                : `Reject the request; ${formatCurrency(decision?.row?.amount || 0)} returns to the promoter's available balance.`}
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder={decision?.action === 'approve' ? 'Payment reference (UTR / txn id) — optional' : 'Reason — optional'}
            value={decisionInput}
            onChange={(e) => setDecisionInput(e.target.value)}
            className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2F2F2]"
            data-testid="decision-input"
          />
          <DialogFooter>
            <Button onClick={submitDecision} className={decision?.action === 'approve' ? 'bg-[#2FBF71] text-[#0A1A0E]' : 'bg-[#E66666] text-white'} data-testid="decision-submit">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Models tab ──────────────────────────────────────────────────────────────
function ModelsTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const fetch = async () => {
    try {
      const { data } = await promoterApi.adminModelsList();
      setRows(data.rows || []);
    } catch {
      toast.error('Failed to load commission models');
    }
  };
  useEffect(() => { fetch(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[#B9B9B9] text-sm">Commission models — per-weekday threshold + rate.</p>
        <Button onClick={() => setCreating(true)} className="bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08]"><Plus size={16} className="mr-1" /> New Model</Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {rows.map((m) => (
          <div key={m._id} className="surface-card border border-[#2A2A2A] rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="text-[#F2F2F2] font-medium">{m.name}</h4>
                <p className="text-[10px] text-[#6F6F6F]">{m.is_default ? 'Default model' : ''}</p>
              </div>
              <div className="flex items-center gap-1">
                <span className={`text-[10px] uppercase px-2 py-1 rounded-full border ${m.active ? 'bg-[#0E2E1E] text-[#2FBF71] border-[#1F4D33]' : 'bg-[#1A1A1A] text-[#6F6F6F] border-[#2A2A2A]'}`}>
                  {m.active ? 'Active' : 'Inactive'}
                </span>
                <Button size="sm" variant="ghost" onClick={() => setEditing(m)} className="text-[#B9B9B9] hover:text-[#F2F2F2]" data-testid={`edit-model-${m._id}`}>
                  <Edit3 size={14} />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 mt-2">
              {PY_WEEKDAYS.map(([k, lbl]) => {
                const rule = (m.rules || {})[k] || {};
                const above = rule.above_percent || 0;
                const below = rule.below_percent || 0;
                return (
                  <div key={k} className="text-center bg-[#0A0A0A] rounded p-1 border border-[#2A2A2A]">
                    <div className="text-[9px] text-[#6F6F6F] uppercase">{lbl}</div>
                    <div className="text-[10px] text-[#F2F2F2]">{above}%</div>
                    <div className="text-[8px] text-[#6F6F6F]">{below}%↓</div>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-[#6F6F6F] mt-2">Threshold: {formatCurrency((m.rules || {})['4']?.threshold || 20000)}</p>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="col-span-2 text-center py-10 text-[#6F6F6F] text-sm">No models yet.</div>
        )}
      </div>

      {(editing || creating) && (
        <ModelEditor
          model={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); fetch(); }}
        />
      )}
    </div>
  );
}

function ModelEditor({ model, onClose, onSaved }) {
  const seed = useMemo(() => {
    if (model?.rules) return model.rules;
    // Sensible new-model defaults: Fri/Sat/Sun 15/20, threshold 20k, others 0
    const r = {};
    PY_WEEKDAYS.forEach(([k]) => {
      if (k === '4' || k === '6') r[k] = { threshold: 20000, below_percent: 15, above_percent: 20 };
      else if (k === '5')          r[k] = { threshold: 20000, below_percent: 10, above_percent: 15 };
      else                          r[k] = { threshold: 20000, below_percent: 0,  above_percent: 0 };
    });
    return r;
  }, [model]);
  const [name, setName] = useState(model?.name || '');
  const [active, setActive] = useState(model?.active ?? true);
  const [rules, setRules] = useState(seed);
  const [busy, setBusy] = useState(false);

  const setVal = (day, field, value) => setRules((r) => ({ ...r, [day]: { ...r[day], [field]: parseInt(value, 10) || 0 } }));

  const submit = async () => {
    if (!name.trim()) { toast.error('Name required'); return; }
    setBusy(true);
    try {
      const payload = { name: name.trim(), rules, active };
      if (model) await promoterApi.adminModelsUpdate(model._id, payload);
      else        await promoterApi.adminModelsCreate(payload);
      toast.success('Model saved');
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111111] border-[#2A2A2A] max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#F2F2F2]">{model ? 'Edit' : 'New'} Commission Model</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <Label className="text-xs text-[#B9B9B9]">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2F2F2] mt-1" data-testid="model-name-input" />
            </div>
            <label className="flex items-center gap-2 pb-2 cursor-pointer">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="accent-[#D4A843]" />
              <span className="text-xs text-[#B9B9B9]">Active</span>
            </label>
          </div>
          <div className="grid gap-2">
            <div className="grid grid-cols-[80px_1fr_1fr_1fr] gap-2 text-[10px] uppercase tracking-wider text-[#6F6F6F]">
              <div>Day</div><div>Threshold (₹)</div><div>Below %</div><div>Above %</div>
            </div>
            {PY_WEEKDAYS.map(([k, lbl]) => (
              <div key={k} className="grid grid-cols-[80px_1fr_1fr_1fr] gap-2 items-center">
                <div className="text-[#F2F2F2]">{lbl}</div>
                <Input type="number" value={rules[k]?.threshold || 0} onChange={(e) => setVal(k, 'threshold', e.target.value)} className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2F2F2] h-8" />
                <Input type="number" value={rules[k]?.below_percent || 0} onChange={(e) => setVal(k, 'below_percent', e.target.value)} className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2F2F2] h-8" />
                <Input type="number" value={rules[k]?.above_percent || 0} onChange={(e) => setVal(k, 'above_percent', e.target.value)} className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2F2F2] h-8" />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08]" data-testid="model-save-button">{busy ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Applications tab (Phase P2) ─────────────────────────────────────────────
function ApplicationsTab() {
  const [rows, setRows]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);    // application_id

  const fetch = async () => {
    setLoading(true);
    try {
      const { data } = await promoterApi.adminAppsList({ status: filter });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load applications'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[160px] bg-[#0A0A0A] border-[#2A2A2A] text-[#F2F2F2]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#111111] border-[#2A2A2A] text-[#F2F2F2]">
            <SelectItem value="pending">Pending review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="ghost" onClick={fetch} className="text-[#B9B9B9] hover:text-[#F2F2F2]"><RefreshCw size={14} /></Button>
        <div className="ml-auto"><InvitesQuickAction /></div>
      </div>

      <div className="surface-card border border-[#2A2A2A] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A]">
              <tr className="border-b border-[#2A2A2A] text-[10px] uppercase tracking-wider text-[#6F6F6F]">
                <th className="py-3 px-3 text-left">Submitted</th>
                <th className="py-3 px-3 text-left">Applicant</th>
                <th className="py-3 px-3 text-left">PAN</th>
                <th className="py-3 px-3 text-left">Bank</th>
                <th className="py-3 px-3 text-left">Invite</th>
                <th className="py-3 px-3 text-left">Status</th>
                <th className="py-3 px-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && [1, 2].map((i) => (
                <tr key={i}><td colSpan="7" className="py-3 px-3"><Skeleton className="h-6 bg-[#1A1A1A]" /></td></tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr><td colSpan="7" className="text-center py-10 text-[#6F6F6F]">No applications in this state.</td></tr>
              )}
              {!loading && rows.map((r) => (
                <tr key={r._id} className="border-b border-[#2A2A2A] hover:bg-[#181818]">
                  <td className="py-3 px-3 text-[10px] text-[#6F6F6F]">{new Date(r.created_at).toLocaleString('en-IN')}</td>
                  <td className="py-3 px-3">
                    <div className="text-[#F2F2F2]">{r.name}</div>
                    <div className="text-[10px] text-[#6F6F6F]">{r.phone}{r.instagram_handle ? ` · @${r.instagram_handle}` : ''}</div>
                  </td>
                  <td className="py-3 px-3 font-mono text-[#B9B9B9]">{r.pan_number}</td>
                  <td className="py-3 px-3 font-mono text-[#B9B9B9]">{r.bank_account_number}</td>
                  <td className="py-3 px-3 font-mono text-[10px] text-[#D4A843]">{r.invite_code}</td>
                  <td className="py-3 px-3"><span className={`text-[10px] uppercase px-2 py-1 rounded-full border ${
                    r.status === 'approved' ? STATUS_PILL.active
                      : r.status === 'rejected' ? STATUS_PILL.suspended
                        : STATUS_PILL.pending
                  }`}>{r.status}</span></td>
                  <td className="py-3 px-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setReviewing(r._id)} className="text-[#D4A843] hover:bg-[#D4A843]/10" data-testid={`review-app-${r._id}`}>
                      Review
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 text-[10px] text-[#6F6F6F] border-t border-[#2A2A2A]">{total} applications</div>
      </div>

      {reviewing && (
        <ReviewApplicationDialog
          applicationId={reviewing}
          onClose={() => setReviewing(null)}
          onActioned={() => { setReviewing(null); fetch(); }}
        />
      )}
    </div>
  );
}

function ReviewApplicationDialog({ applicationId, onClose, onActioned }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [vanity, setVanity] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    promoterApi.adminAppDetail(applicationId).then(({ data }) => setDetail(data))
      .catch(() => toast.error('Failed to load application'));
  }, [applicationId]);

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await promoterApi.adminAppApprove(applicationId, { vanity_code: vanity.trim() || null });
      toast.success(`Approved → ${data.promoter.code}`);
      onActioned?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || 'Approval failed');
    } finally { setBusy(false); }
  };
  const reject = async () => {
    if (!rejectReason.trim()) { toast.error('Provide a rejection reason'); return; }
    setBusy(true);
    try {
      await promoterApi.adminAppReject(applicationId, rejectReason.trim());
      toast.success('Application rejected');
      onActioned?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || 'Reject failed');
    } finally { setBusy(false); }
  };

  if (!detail) {
    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="bg-[#111111] border-[#2A2A2A]"><Skeleton className="h-32 bg-[#1A1A1A]" /></DialogContent>
      </Dialog>
    );
  }

  const panUrl = detail.pan_signed_url;
  const ndaUrl = detail.nda_signed_url;
  const ndaDeliverable = ndaUrl;
  const isPending = detail.status === 'pending';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111111] border-[#2A2A2A] max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#F2F2F2]">Application · {detail.name}</DialogTitle>
          <DialogDescription className="text-[#B9B9B9]">
            Submitted {new Date(detail.created_at).toLocaleString('en-IN')} · status <b className="text-[#F2F2F2] uppercase">{detail.status}</b>
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left: KYC fields */}
          <div className="space-y-2 text-sm">
            <DetailRow label="Name"      value={detail.name} />
            <DetailRow label="Phone"     value={detail.phone} />
            <DetailRow label="Instagram" value={detail.instagram_handle ? `@${detail.instagram_handle}` : '—'} />
            <DetailRow label="PAN"       value={detail.pan_number} mono />
            <DetailRow label="Bank A/C"  value={detail.bank_account_number} mono />
            <DetailRow label="IFSC"      value={detail.ifsc_code} mono />
            <DetailRow label="Invite"    value={detail.invite_code} mono />
            <DetailRow label="IP"        value={detail.nda_ip || '—'} mono />
            <DetailRow label="NDA SHA-256" value={detail.nda_signature_hash ? detail.nda_signature_hash.slice(0, 16) + '…' : '—'} mono />
            <DetailRow label="Signed at"  value={detail.nda_signed_at ? new Date(detail.nda_signed_at).toLocaleString('en-IN') : '—'} />
          </div>
          {/* Right: documents */}
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[#B9B9B9] mb-1 block">PAN document</Label>
              {panUrl
                ? (panUrl.includes('.pdf')
                    ? <iframe src={panUrl} title="PAN preview" className="w-full h-56 bg-black rounded-md border border-[#2A2A2A]" />
                    : <img src={panUrl} alt="PAN" className="w-full max-h-56 object-contain bg-black rounded-md border border-[#2A2A2A]" />
                  )
                : <div className="text-[#6F6F6F] text-xs">No file attached</div>}
            </div>
            {ndaDeliverable && (
              <a
                href={ndaDeliverable}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 text-[#D4A843] hover:underline text-sm"
                data-testid="nda-pdf-link"
              >
                <FileText size={14} /> Open signed NDA (PDF) <ExternalLink size={12} />
              </a>
            )}
          </div>
        </div>

        {isPending && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 pt-4 border-t border-[#2A2A2A]">
            <div className="space-y-2">
              <Label className="text-xs text-[#B9B9B9]">Vanity code (optional)</Label>
              <Input value={vanity} onChange={(e) => setVanity(e.target.value.toUpperCase())} placeholder="leave blank → auto PRM-XXXXX" className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2F2F2] uppercase font-mono" data-testid="vanity-input" />
              <Button onClick={approve} disabled={busy} className="w-full bg-[#2FBF71] hover:bg-[#3FCF81] text-[#0A1A0E]" data-testid="approve-button">
                {busy ? 'Approving…' : 'Approve & Create Promoter'}
              </Button>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-[#B9B9B9]">Reject reason</Label>
              <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Audit log (required)" className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2F2F2]" data-testid="reject-reason-input" />
              <Button onClick={reject} disabled={busy} variant="outline" className="w-full border-[#E66666]/40 text-[#E66666] hover:bg-[#3D1A1A]" data-testid="reject-button">
                Reject
              </Button>
            </div>
          </div>
        )}
        {!isPending && detail.rejection_reason && (
          <div className="mt-3 p-3 rounded bg-[#3D1A1A] border border-[#5A2E2E] text-[#E66666] text-xs">
            <b>Rejected:</b> {detail.rejection_reason}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 py-1 border-b border-[#1A1A1A]">
      <span className="text-[10px] uppercase tracking-wider text-[#6F6F6F]">{label}</span>
      <span className={`text-[#F2F2F2] ${mono ? 'font-mono text-xs' : ''} truncate`}>{value}</span>
    </div>
  );
}

function InvitesQuickAction() {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastCode, setLastCode] = useState(null);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await promoterApi.adminInviteCreate({ label: label.trim() || null, ttl_days: 30, max_uses: 1 });
      setLastCode(data.code);
      toast.success(`Invite ${data.code} created · 30-day TTL`);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || 'Create failed');
    } finally { setBusy(false); }
  };

  const copy = () => { navigator.clipboard.writeText(lastCode); toast.success('Copied'); };
  const copyLink = () => {
    const u = `${window.location.origin}/promoters/apply?invite=${lastCode}`;
    navigator.clipboard.writeText(u); toast.success('Apply link copied');
  };

  return (
    <>
      <Button onClick={() => { setOpen(true); setLastCode(null); setLabel(''); }} className="bg-[#1A1A1A] border border-[#2A2A2A] text-[#F2F2F2] hover:bg-[#2A2A2A]" data-testid="create-invite-button">
        <Plus size={14} className="mr-1" /> Invite
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111111] border-[#2A2A2A]">
          <DialogHeader>
            <DialogTitle className="text-[#F2F2F2]">Create Invite Code</DialogTitle>
            <DialogDescription className="text-[#B9B9B9]">Single-use, expires in 30 days.</DialogDescription>
          </DialogHeader>
          {!lastCode ? (
            <div className="space-y-3">
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Recipient name / email (optional)" className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2F2F2]" data-testid="invite-label-input" />
              <Button onClick={submit} disabled={busy} className="w-full bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08]">
                {busy ? 'Creating…' : 'Create Invite'}
              </Button>
            </div>
          ) : (
            <div className="space-y-3 text-center">
              <p className="text-[#B9B9B9] text-sm">Share this code with the applicant:</p>
              <div className="font-mono text-2xl tracking-widest text-[#D4A843] bg-[#0A0A0A] border border-[#D4A843]/30 rounded-md py-3" data-testid="invite-code-output">{lastCode}</div>
              <div className="flex gap-2">
                <Button onClick={copy}     variant="outline" className="flex-1 border-[#2A2A2A] text-[#F2F2F2] hover:bg-[#1A1A1A]"><Copy size={14} className="mr-1" /> Copy code</Button>
                <Button onClick={copyLink} variant="outline" className="flex-1 border-[#2A2A2A] text-[#F2F2F2] hover:bg-[#1A1A1A]"><Copy size={14} className="mr-1" /> Copy apply link</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}


// ─── Root ────────────────────────────────────────────────────────────────────
export default function PromoterManagerPage() {
  const [tab, setTab] = useState('applications');
  return (
    <AdminShell title="Promoters">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#0A0A0A] border border-[#2A2A2A]">
          <TabsTrigger value="applications" className="data-[state=active]:bg-[#D4A843] data-[state=active]:text-[#120F08]" data-testid="tab-applications">
            <FileText size={14} className="mr-1.5" /> Applications
          </TabsTrigger>
          <TabsTrigger value="promoters" className="data-[state=active]:bg-[#D4A843] data-[state=active]:text-[#120F08]" data-testid="tab-promoters">
            <Users size={14} className="mr-1.5" /> Promoters
          </TabsTrigger>
          <TabsTrigger value="payouts" className="data-[state=active]:bg-[#D4A843] data-[state=active]:text-[#120F08]" data-testid="tab-payouts">
            <BadgeIndianRupee size={14} className="mr-1.5" /> Payouts
          </TabsTrigger>
          <TabsTrigger value="models" className="data-[state=active]:bg-[#D4A843] data-[state=active]:text-[#120F08]" data-testid="tab-models">
            <Settings size={14} className="mr-1.5" /> Models
          </TabsTrigger>
        </TabsList>
        <TabsContent value="applications" className="mt-4"><ApplicationsTab /></TabsContent>
        <TabsContent value="promoters"    className="mt-4"><PromotersTab /></TabsContent>
        <TabsContent value="payouts"      className="mt-4"><PayoutsTab /></TabsContent>
        <TabsContent value="models"       className="mt-4"><ModelsTab /></TabsContent>
      </Tabs>
    </AdminShell>
  );
}
